<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-9">
        <div class="card card-body mb-3">
          <v-chart-bar-events
            v-if="dailyAggregations"
            :labels="dailyAggregations.map((el) => (el.date))"
            :logins="dailyAggregations.map((el) => (el.login_attempts))"
            :reviews="dailyAggregations.map((el) => (el.reviews_completed))"
            :product-orders="dailyAggregations.map((el) => (el.product_orders))"
          />
        </div>
      </div>
      <div class="col-md-3">
        <div class="card card-body text-center">
          <div
            v-if="aggregations"
            class="d-flex flex-column justify-content-between"
            style="height: 400px"
          >
            <div>
              <h1 class="m-0">
                {{ aggregations.reviews_total }}
              </h1>
              <span class="text-muted">
                {{ $t('word.reviews_total').toLowerCase() }}
              </span>
            </div>
            <hr class="w-100" />
            <div>
              <h1 class="m-0">
                {{ aggregations.reviews_internal }}
              </h1>
              <span class="text-muted">
                {{ $t('word.reviews_internal').toLowerCase() }}
              </span>
            </div>
            <hr class="w-100" />
            <div>
              <h1 class="m-0">{{ aggregations.users_total }}</h1>
              <span class="text-muted">
                {{ $tc('word.user', aggregations.users_total).toLowerCase() }}
              </span>
            </div>
            <hr class="w-100" />
            <div>
              <h1 class="m-0">{{ aggregations.users_active }}</h1>
              <span class="text-muted">
                {{ $t('phrase.active_users').toLowerCase() }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-4">
        <div class="card mb-3" v-if="upcomingResearches">
          <div class="card-header">
            <h3 class="m-0">
              <i class="fa fa-search mr-3"></i>
              {{ $t('phrase.upcoming_researches') }}
            </h3>
          </div>
          <table class="table">
            <tbody>
            <tr v-for="research in upcomingResearches" :key="research.id">
              <td>
                <div>{{ research.organisation.name }}</div>
                <small class="text-muted">{{ research.title }}</small>
              </td>
              <td :class="withinWeekClass(research.start_date)">
                {{ getDateDifferenceInDays(research.start_date) }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="card">
          <div class="card-header">
            <h3 class="m-0">
              <i class="fa fa-coins mr-3"></i>
              {{ $t('word.coins') }}
            </h3>
          </div>
          <div class="card-body" v-if="coinStatistics">
            <h4>{{ $t('phrase.coins_in_coin_accounts') }}</h4>
            <table class="table table-bordered">
              <tr>
                <td class="w-50">
                  {{ $t('phrase.total_issued') }}
                  <small>({{
                      $t('phrase.fake_accounts_excluded')
                        .toLowerCase()
                    }})</small>
                </td>
                <td class="text-right">
                  {{ coinStatistics.coins_issued_without_fake }}
                </td>
                <td class="text-right">
                  {{ toPrettyPrice(coinStatistics.coins_issued_without_fake / 100) }}
                </td>
              </tr>
              <tr>
                <td>{{ $t('phrase.total_spent') }}</td>
                <td class="text-right">{{ coinStatistics.coins_spent }}</td>
                <td class="text-right">
                  {{ toPrettyPrice(coinStatistics.coins_spent / 100) }}
                </td>
              </tr>
              <tr>
                <td>
                  <strong>{{ $t('phrase.remaining_in_accounts') }}</strong>
                </td>
                <td class="text-right">
                  <strong>{{ coinStatistics.coins_in_accounts }}</strong>
                </td>
                <td class="text-right">
                  <strong>{{ toPrettyPrice(coinStatistics.coins_in_accounts / 100) }}</strong>
                </td>
              </tr>
            </table>
            <h4>
              {{ $t('phrase.coins_in_commercials') }}
            </h4>

            <table class="table table-bordered mb-0 pb-0">
              <tr>
                <td class="w-50">{{ $t('phrase.total_in_commercials') }}</td>
                <td class="text-right">{{ coinStatistics.coins_to_be_earned }}</td>
                <td class="text-right">
                  {{ toPrettyPrice(coinStatistics.coins_to_be_earned / 100) }}
                </td>
              </tr>
              <tr>
                <td>{{ $t('phrase.total_spent') }}</td>
                <td class="text-right">{{ coinStatistics.coins_issued }}</td>
                <td class="text-right">
                  {{ toPrettyPrice(coinStatistics.coins_issued / 100) }}
                </td>
              </tr>
              <tr>
                <td><strong>{{ $t('phrase.remaining_in_commercials') }}</strong></td>
                <td class="text-right">
                  <strong>{{ coinStatistics.remaining_coins }}</strong>
                </td>
                <td class="text-right">
                  <strong>{{ toPrettyPrice(coinStatistics.remaining_coins / 100) }}</strong>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
      <div class="col-lg-8">
        <v-user-list/>
      </div>
    </div>
  </div>
</template>

<script>
import { toPrettyPrice } from '@/common/utils/formatting';
import {
  fetchAggregations,
  fetchAggregationsPerDay,
  fetchCoinStatistics,
  fetchUpcomingResearches,
} from '@/admin/api';
import {
  getDateDifferenceInDays,
  getPrettyDateDifferenceInDays,
} from '@/common/utils/datetime';
import { mapGetters } from 'vuex';

import UserList from '../components/UserList.vue';
import ChartBarEvents from '../components/ChartBarEvents.vue';

export default {
  name: 'Dashboard',
  components: {
    'v-user-list': UserList,
    'v-chart-bar-events': ChartBarEvents,
  },
  data() {
    return {
      coinStatistics: null,
      aggregations: null,
      dailyAggregations: null,
      upcomingResearches: null,
    };
  },
  computed: {
    ...mapGetters(['user']),
  },
  methods: {
    toPrettyPrice,
    getDateDifferenceInDays(d) {
      return getPrettyDateDifferenceInDays(new Date(), new Date(d));
    },
    isWithinWeek(startDate) {
      const days = getDateDifferenceInDays(new Date(), new Date(startDate));
      return days < 7;
    },
    withinWeekClass(startDate) {
      if (this.isWithinWeek(startDate)) {
        return ['text-danger', 'text-bold'];
      }
      return [];
    },
    async fetchCoinStatistics() {
      const response = await fetchCoinStatistics();
      this.coinStatistics = response.data;
    },
    async fetchAggregations() {
      const response = await fetchAggregations();
      this.aggregations = response.data;
      setTimeout(this.fetchAggregations, 1000 * 60);
    },
    async fetchAggregationsPerDay() {
      const response = await fetchAggregationsPerDay();
      this.dailyAggregations = response.data;
    },
    async fetchUpcomingResearches() {
      const response = await fetchUpcomingResearches();
      this.upcomingResearches = response.data;
    },
  },
  created() {
    this.fetchCoinStatistics();
    this.fetchAggregations();
    this.fetchAggregationsPerDay();
    this.fetchUpcomingResearches();
  },
};
</script>
